import {
  SpeechIcon,
  ArrowUp,
  EraserIcon,
  UserIcon,
  PencilLineIcon,
  LockIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Textarea } from "../ui/textarea";
import { RoundedCountryFlag } from "../profile/RoundedCountryFlag";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Drawer } from "vaul";
import { LanguageResponse, SubscriptionStatus } from "@/generated-api";
import {
  useActiveSubscription,
  useGetUserProfile,
  useSendMessage,
} from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import SpinnerSmall from "../SpinnerSmall";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import pinWhite from "../../assets/pin-white.svg";
import pinGray from "../../assets/pin-gray.svg";
import Spinner from "../Spinner";
import { useTranslation } from "@/hooks/useTranslation";
import { TranslationTree } from "@/translations";
import { useNavigate } from "react-router-dom";
import { mapAlpha2 } from "@/lib/utils";
import { useHaptics } from "@/hooks/useHaptics";

type NewMessageDrawerDialogProps = {
  profile_id: string;
  display_name: string | null;
  photo_url: string;
  age: number | null;
  city_name: string;
  country_alpha_2: string;
  languages: LanguageResponse[] | null;
};

const messageFormSchema = (t: TranslationTree) =>
  z.object({
    content: z
      .string()
      .min(1, t.MESSAGE_DRAWER.ERROR.MESSAGE_EMPTY)
      .max(250, t.MESSAGE_DRAWER.ERROR.MESSAGE_TOO_LONG),
  });

type MessageFormSchema = z.infer<ReturnType<typeof messageFormSchema>>;

function NewMessageContent({
  profile,
  onClose,
  t,
  isSupport,
}: {
  profile: NewMessageDrawerDialogProps;
  onClose: () => void;
  t: TranslationTree;
  isSupport: boolean;
}) {
  const { toast } = useToast();
  const { simpleTrigger } = useHaptics();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const newMessageMutation = useSendMessage(isSupport);
  const userProfileQuery = useGetUserProfile();

  const schema = messageFormSchema(t);
  const form = useForm<MessageFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      content: "",
    },
  });

  const handleSendMessage = (values: MessageFormSchema) => {
    onClose();
    simpleTrigger();
    newMessageMutation.mutate(
      {
        counterparty_id: profile.profile_id,
        content: values.content,
      },
      {
        onError: (error) => {
          console.error("Error - could not message user", error);
          toast({
            variant: "destructive",
            title: t.MESSAGE_DRAWER.ERROR.SEND,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col p-6 md:pt-4 md:p-0 pb-2">
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Avatar className="h-20 w-20">
            <AvatarImage
              src={profile.photo_url}
              alt="profileImage"
              className="object-cover scale-125"
              loading="eager"
            />
            <AvatarFallback>
              <UserIcon size={20} className="text-gray-400" />
            </AvatarFallback>
          </Avatar>
          <div className="ml-4 flex flex-col gap-y-2">
            <div className="text-lg whitespace-nowrap">
              {profile.display_name}, {profile.age}
            </div>
            <div className="flex flex-row gap-1">
              <img src={pinWhite} className="w-5 h-5" />
              <span className="text-sm">
                {profile.city_name}, {mapAlpha2(profile.country_alpha_2)}
              </span>
            </div>
          </div>
        </div>
        {profile.languages && profile.languages.length > 0 && (
          <div className="flex flex-row justify-end">
            <div className="flex flex-row items-center">
              <SpeechIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              <div className="flex flex-wrap max-w-22 gap-y-3 items-center">
                {profile.languages.map((language) => (
                  <div key={language.id} className="relative flex items-center">
                    <RoundedCountryFlag
                      alpha_2={language.flag_code}
                      className="h-6 w-6 rounded-full"
                    />
                    <div className="h-6 w-6 absolute inset-0 shadow-[inset_0_2px_3px_rgba(0,0,0,0.5)] rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSendMessage)}
        >
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder={t.MESSAGE_DRAWER.LABELS.MESSAGE_PLACEHOLDER(
                      profile.display_name,
                    )}
                    className="mt-6 resize-none min-h-24 rounded-xl text-base"
                    maxLength={250}
                    {...field}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && isDesktop) {
                        e.preventDefault();
                        void form.handleSubmit(handleSendMessage)();
                      }
                    }}
                  />
                </FormControl>
                <FormMessage className="mt-1" />
              </FormItem>
            )}
          />
          <div className="flex flex-row mt-4 justify-between items-center">
            <Button
              variant="secondary"
              type="button"
              className="rounded-full w-10 h-10 p-0"
              onClick={() => form.setValue("content", "")}
            >
              <EraserIcon className="h-5 w-5" />
            </Button>
            <div className="flex flex-row gap-4 items-center">
              {userProfileQuery.data?.intro !== null && (
                <>
                  {isDesktop ? (
                    <Button
                      type="button"
                      className="rounded-full"
                      variant="secondary"
                      onClick={() => {
                        form.setValue(
                          "content",
                          userProfileQuery.data?.intro || "",
                          {
                            shouldDirty: true,
                            shouldTouch: true,
                          },
                        );
                      }}
                    >
                      <PencilLineIcon className="h-5 w-5 mr-2" />
                      Intro
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="icon"
                      className="rounded-full"
                      variant="secondary"
                      onClick={() => {
                        form.setValue(
                          "content",
                          userProfileQuery.data?.intro || "",
                          {
                            shouldDirty: true,
                            shouldTouch: true,
                          },
                        );
                      }}
                    >
                      <PencilLineIcon className="h-5 w-5" />
                    </Button>
                  )}
                </>
              )}
              {isDesktop ? (
                <Button
                  type="submit"
                  className="rounded-full"
                  disabled={
                    newMessageMutation.isPending ||
                    !form.formState.isDirty ||
                    form.watch("content").length === 0
                  }
                >
                  {newMessageMutation.isPending ? (
                    <SpinnerSmall black={true} />
                  ) : (
                    <>
                      <ArrowUp className="h-5 w-5 mr-2" />
                      {t.MESSAGE_DRAWER.LABELS.SEND_BUTTON}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="icon"
                  className="rounded-full"
                  disabled={
                    newMessageMutation.isPending ||
                    !form.formState.isDirty ||
                    form.watch("content").length === 0
                  }
                >
                  {newMessageMutation.isPending ? (
                    <SpinnerSmall black={true} />
                  ) : (
                    <ArrowUp className="h-5 w-5" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

function MessageUpgradeContent({
  profile,
  t,
}: {
  profile: NewMessageDrawerDialogProps;
  t: TranslationTree;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col p-6 md:pt-4 md:p-0 pb-2">
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Avatar className="h-20 w-20">
            <AvatarImage
              src={profile.photo_url}
              alt="profileImage"
              className="object-cover scale-125 opacity-60"
              loading="eager"
            />
            <AvatarFallback>
              <UserIcon size={20} className="text-gray-400" />
            </AvatarFallback>
          </Avatar>
          <div className="ml-4 flex flex-col gap-y-2">
            <div className="text-lg text-gray-400 whitespace-nowrap">
              {profile.display_name}, {profile.age}
            </div>
            <div className="flex flex-row gap-1 text-gray-400">
              <img src={pinGray} className="w-5 h-5" />
              <span className="text-sm">
                {profile.city_name}, {profile.country_alpha_2}
              </span>
            </div>
          </div>
        </div>
        {profile.languages && profile.languages.length > 0 && (
          <div className="flex flex-row justify-end">
            <div className="flex flex-row items-center">
              <SpeechIcon className="h-5 w-5 mr-2 flex-shrink-0" />
              <div className="flex flex-wrap max-w-22 gap-y-3 items-center">
                {profile.languages.map((language) => (
                  <div key={language.id} className="relative flex items-center">
                    <RoundedCountryFlag
                      alpha_2={language.flag_code}
                      className="h-6 w-6 rounded-full"
                    />
                    <div className="h-6 w-6 absolute inset-0 shadow-[inset_0_2px_3px_rgba(0,0,0,0.5)] rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Textarea
        placeholder={t.MESSAGE_DRAWER.LABELS.UPGRADE_PLACEHOLDER(
          profile.display_name,
        )}
        className="mt-6 resize-none min-h-24 rounded-xl text-base hover:cursor-default disabled:hover:cursor-default"
        maxLength={250}
        disabled={true}
      />
      <div className="flex flex-row mt-4 justify-between items-center">
        <Button
          variant="secondary"
          type="button"
          className="rounded-full w-10 h-10 p-0"
          disabled={true}
        >
          <EraserIcon className="h-5 w-5" />
        </Button>
        <div className="flex flex-row items-center">
          <Button
            type="submit"
            className="rounded-full"
            onClick={() => navigate("/upgrade")}
          >
            <LockIcon className="h-5 w-5 mr-2" />
            {t.MESSAGE_DRAWER.LABELS.UPGRADE}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function NewMessageDrawerDialog({
  profile,
  open,
  onClose,
  isSupport = false,
}: {
  profile: NewMessageDrawerDialogProps;
  open: boolean;
  onClose: () => void;
  isSupport?: boolean;
}) {
  const activeSubscriptionQuery = useActiveSubscription();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const t = useTranslation();

  const renderContent = () => {
    if (activeSubscriptionQuery.isLoading) return <Spinner />;
    if (activeSubscriptionQuery.isError) {
      return (
        <div className="text-gray-500 p-4">
          {t.MESSAGE_DRAWER.ERROR.MEMBERSHIP}
        </div>
      );
    }
    return activeSubscriptionQuery.data?.subscription_status ===
      SubscriptionStatus.ACTIVE ||
      activeSubscriptionQuery.data?.subscription_status ===
        SubscriptionStatus.ACTIVE_CANCELED ||
      isSupport ? (
      <NewMessageContent
        profile={profile}
        onClose={onClose}
        t={t}
        isSupport={isSupport}
      />
    ) : (
      <MessageUpgradeContent profile={profile} t={t} />
    );
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="dark text-white">
          <DialogHeader className="hidden">
            <DialogTitle></DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          {renderContent()}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer.Root
      open={open}
      onClose={onClose}
      repositionInputs={false}
      direction="top"
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40" />
        <Drawer.Title className="hidden"></Drawer.Title>
        <Drawer.Description className="hidden"></Drawer.Description>
        <Drawer.Content
          className="flex flex-col fixed left-0 right-0 max-h-[82vh] z-50 max-w-lg w-full mx-auto focus:outline-none"
          style={
            {
              "--initial-transform": "calc(100% + 40px)",
              top: "max(2.5rem, calc(env(safe-area-inset-top) + 1rem))",
            } as React.CSSProperties
          }
        >
          <div className="bg-black dark text-white rounded-3xl border border-zinc-600 pb-4 focus:outline-none">
            {renderContent()}
            <Drawer.Handle className="select-none" />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
